import React from "react";
import { ErrorInfoYellowIcon } from "../../assets/Icons";
import { IIconBasicProps } from "../../assets/Icons/IIconBasicProps";
import Button from "../../components/common/button/button";
import { getStoreCardMessage } from "./ez-quote.helpers";
import { Item } from "./ez-quote.types";
import { useTranslation } from "../../contexts/TranslationContext";
import { isDesignerReviewBrand } from "../../constants/brandFeatures";

/**
 * Common component for expert review warning cards
 */
export const ExpertReviewWarningCard: React.FC<{
  message: string;
  bullets?: string[];
  iconSize?: number;
  className?: string;
}> = ({ message, bullets, iconSize = 16, className = "" }) => {
  return (
    <div className={`expert-review-warning-card ${className}`}>
      <div className="warning-icon">
        <ErrorInfoYellowIcon width={iconSize} height={iconSize} />
      </div>
      <div className="warning-content">
        <div className="warning-message">{message}</div>
        {bullets && bullets.length > 0 && (
          <ul className="warning-bullets">
            {bullets.map((bullet, i) => (
              <li key={i}>{bullet}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/**
 * Renders a card for an item summary.
 * Now accepts optional onEdit and onDelete callbacks.
 */
export const ItemCard: React.FC<{
  item: Item;
  getIconComponent: (iconName: string | null) => React.FC<IIconBasicProps>;
  isFinalReview?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  setEstimateCard?: () => void;
  openEstimateCardFlag?: boolean;
  brand?: string | null;
}> = ({
  item,
  getIconComponent,
  isFinalReview,
  onEdit,
  onDelete,
  setEstimateCard,
  openEstimateCardFlag,
  brand,
}) => {
  const t = useTranslation();
  const isJewelryItem = !!item.parentCategoryName;
  const iconName = isJewelryItem
    ? (item.goldParentCategory?.icon_name ?? item.category.icon_name)
    : item.category.icon_name;
  const IconComponent = getIconComponent(iconName);

  // Branded/Designer flag only changes behavior for designer-review brands; other brands keep the normal flow.
  const isBrandedOrDesigner =
    isDesignerReviewBrand(brand) &&
    Object.entries(item.attributes).some(
      ([key, val]) => key.toLowerCase().includes("branded") && val.id?.toLowerCase() === "yes"
    );
  const needsExpertReview = item.estimate === "N/A" || isBrandedOrDesigner;
  const reviewMessage = isBrandedOrDesigner
    ? t.finalReview.brandedDesignerItemNote
    : getStoreCardMessage(item, t);

  return (
    <div
      className={`summary-item-card ${
        isFinalReview && needsExpertReview ? "has-expert-review" : ""
      }`}
    >
      {isJewelryItem ? (
        <div className="gold-item-card-header">
          <div className="item-icon-wrapper gold-item-icon">
            <IconComponent width={44} height={44} />
          </div>
          <div className="gold-item-header-text">
            <div className="category-name">{item.parentCategoryName}</div>
            <div className="attribute-name">
              {[item.goldTypeName ?? "Gold", item.itemTypeLabel ?? item.category.category_name].filter(Boolean).join(" ")}
            </div>
          </div>
        </div>
      ) : (
        <div className="item-icon-wrapper">
          <IconComponent width={44} height={44} />
        </div>
      )}
      <div className="item-details">
        {!isJewelryItem && (
          <>
            <div className="category-name">{item.category.category_name}</div>
            {(item.attributes["Manufacturer"]?.name || item.attributes["Model Number"]?.name) && (
              <div className="attribute-name">
                {[item.attributes["Manufacturer"]?.name, item.attributes["Model Number"]?.name]
                  .filter(Boolean)
                  .join(" ")}
              </div>
            )}
          </>
        )}

        {isFinalReview && (
          <>
            {needsExpertReview ? (
              <ExpertReviewWarningCard
                message={reviewMessage}
                iconSize={16}
                className="item-expert-review-card"
              />
            ) : (
              <div className="item-estimate-card">
                <div className="item-estimate-label">{t.itemCard.estimatedValue}</div>
                <div className="item-estimate">{item.estimate}</div>
              </div>
            )}
          </>
        )}
        {!needsExpertReview ? (
          <div className="divider"></div>
        ) : (
          <div className="notsuremargin"></div>
        )}
        {(!needsExpertReview || !isFinalReview) && (
          <div className="item-attributes">
            {Object.entries(item.attributes).map(([key, attribute]) => {
              if (item.textAreaAttributeKeys?.includes(key)) return null;

              const displayKey =
                key === "Internal Storage Capacity (GB)" || key === "Internal Storage Capacity"
                  ? t.itemCard.internalStorage
                  : key === "Quality"
                  ? t.itemCard.purity
                  : key;

              if (displayKey !== "Manufacturer" && displayKey !== "Model Number") {
                const attributeValue =
                  attribute.id !== "other" &&
                  (key === "Internal Storage Capacity (GB)" ||
                    key === "Internal Storage Capacity" ||
                    key === "RAM Memory (GB)" ||
                    key === "RAM Memory")
                    ? attribute.name.toUpperCase()
                    : attribute.name;

                return (
                  <div className="attribute" key={key}>
                    <div className="attribute-label">{displayKey}</div>
                    <div className="attribute-value">{attributeValue}</div>
                  </div>
                );
              }
              return null;
            })}
            {item.condition && (
              <div className="attribute">
                <div className="attribute-label">{t.itemCard.condition}</div>
                <div className="attribute-value">{item.condition.title}</div>
              </div>
            )}
          </div>
        )}
        {item.textAreaAttributeKeys?.map((key) => {
          const val = item.attributes[key]?.name;
          if (!val) return null;
          return (
            <div key={key} className="item-notes">
              {val}
            </div>
          );
        })}
      </div>
      {!isFinalReview && (
        <div className="item-actions">
          {onDelete && (
            <div className="delete-action" onClick={onDelete}>
              {t.itemCard.remove}
            </div>
          )}
          {onEdit && (
            <div onClick={onEdit} className="edit-action">
              <Button label={t.itemCard.edit} onClick={onEdit} styleType="outline" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
